/**
 * SubTasksWithAI - Sous-tâches avec suggestions IA intégrées
 * Même design que les sous-tâches manuelles + bouton expansion IA
 */

import React, { useState, useEffect, memo, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ListTodo,
  CheckCircle2,
  Sparkles,
  ChevronDown,
  ChevronUp,
  Loader2,
  ExternalLink,
  RefreshCw,
  Music,
  MapPin,
  Utensils,
  Play
} from 'lucide-react';
import { useAIService } from '../../hooks/useAIService';
import smartLinksService from '../../services/smartLinksService';

// Helper pour extraire le texte d'une instruction (peut être string ou objet)
const getInstructionText = (instruction) => {
  if (!instruction) return '';
  if (typeof instruction === 'string') return instruction;
  if (typeof instruction === 'object' && instruction.text) return instruction.text;
  if (typeof instruction === 'object' && instruction.name) return instruction.name;
  return '';
};

const SubTasksWithAI = memo(function SubTasksWithAI({
  task,
  instructions = [],
  weekOffset,
  currentDayIndex,
  isSubTaskCompleted,
  toggleSubTask,
  getCompletedCount,
  context = {},
  onAllComplete
}) {
  const { generateSubTasks, isProcessing } = useAIService();
  const [showAI, setShowAI] = useState(false);
  const [aiSubTasks, setAiSubTasks] = useState(null);
  const [completedAISteps, setCompletedAISteps] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);

  const hasInstructions = instructions && instructions.length > 0;
  const manualCompletedCount = hasInstructions
    ? getCompletedCount(weekOffset, currentDayIndex, task.id, instructions.length)
    : 0;

  // Charger les sous-tâches IA quand on expand
  useEffect(() => {
    if (showAI && !aiSubTasks && !isLoading) {
      loadAISubTasks();
    }
  }, [showAI]);

  const loadAISubTasks = async () => {
    setIsLoading(true);
    try {
      const data = await generateSubTasks(task, context);
      setAiSubTasks(data);
    } catch (error) {
      console.error('Erreur chargement sous-tâches IA:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAIStepComplete = useCallback((stepId) => {
    setCompletedAISteps(prev => {
      const newSet = new Set(prev);
      if (newSet.has(stepId)) {
        newSet.delete(stepId);
      } else {
        newSet.add(stepId);
      }

      // Vérifier si toutes les étapes IA sont complétées
      if (aiSubTasks?.subTasks && newSet.size === aiSubTasks.subTasks.length) {
        setTimeout(() => {
          if (onAllComplete) onAllComplete(task);
        }, 500);
      }

      return newSet;
    });
  }, [aiSubTasks, onAllComplete, task]);

  const aiCompletedCount = completedAISteps.size;
  const aiTotalSteps = aiSubTasks?.subTasks?.length || 0;

  // Obtenir les liens intelligents pour cette tâche
  const smartLinks = useMemo(() => {
    if (!showAI || !task) return null;
    return smartLinksService.getSmartLinksForTask(task);
  }, [showAI, task]);

  // Icône selon le type de lien
  const getLinkIcon = (type) => {
    switch (type) {
      case 'playlist': return <Music className="w-3 h-3" />;
      case 'place': return <MapPin className="w-3 h-3" />;
      case 'recipe': return <Utensils className="w-3 h-3" />;
      case 'youtube':
      case 'primeVideo': return <Play className="w-3 h-3" />;
      default: return <ExternalLink className="w-3 h-3" />;
    }
  };

  return (
    <div className="subtask-area mt-3 space-y-1 p-2 rounded bg-zinc-800/30" onClick={(e) => e.stopPropagation()}>

      {/* Header avec compteur */}
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-[9px] font-medium uppercase flex items-center gap-1 text-zinc-500">
          <ListTodo className="w-3 h-3" />
          {hasInstructions ? `${manualCompletedCount}/${instructions.length}` : '0/0'}
          {showAI && aiTotalSteps > 0 && (
            <span className="ml-1 text-purple-400">
              + IA {aiCompletedCount}/{aiTotalSteps}
            </span>
          )}
        </p>
      </div>

      {/* Sous-tâches manuelles */}
      {hasInstructions && instructions.map((ins, idx) => {
        const subChecked = isSubTaskCompleted(weekOffset, currentDayIndex, task.id, idx);
        return (
          <div
            key={`manual-${idx}`}
            onClick={(e) => { e.stopPropagation(); toggleSubTask(weekOffset, currentDayIndex, task.id, idx); }}
            className={`flex items-center gap-2 p-1.5 rounded transition-all cursor-pointer ${subChecked ? 'opacity-50' : 'hover:bg-white/5'}`}
          >
            <div className={`w-4 h-4 rounded border flex items-center justify-center transition-all ${subChecked ? 'bg-zinc-600 border-zinc-600 text-white' : 'bg-zinc-800/50 border-zinc-600'}`}>
              {subChecked && <CheckCircle2 className="w-3 h-3" />}
            </div>
            <span className={`text-[11px] font-medium ${subChecked ? 'line-through text-zinc-500' : 'text-zinc-300'}`}>
              {getInstructionText(ins)}
            </span>
          </div>
        );
      })}

      {/* Bouton expansion IA */}
      <button
        onClick={(e) => { e.stopPropagation(); setShowAI(!showAI); }}
        className={`
          w-full mt-2 p-2 rounded flex items-center justify-between
          transition-all text-[10px] font-medium
          ${showAI
            ? 'bg-purple-500/20 border border-purple-500/30 text-purple-300'
            : 'bg-zinc-700/30 border border-zinc-700/50 text-zinc-400 hover:bg-zinc-700/50 hover:text-zinc-300'
          }
        `}
      >
        <span className="flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" />
          {showAI ? 'Étapes IA détaillées' : 'Voir les étapes IA détaillées'}
          {isLoading && <Loader2 className="w-3 h-3 animate-spin" />}
        </span>
        {showAI ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>

      {/* Contenu IA expandé */}
      <AnimatePresence>
        {showAI && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="mt-2 pt-2 border-t border-purple-500/20 space-y-1">

              {/* Message d'encouragement */}
              {aiSubTasks?.encouragement && (
                <div className="p-2 rounded bg-purple-500/10 border border-purple-500/20 mb-2">
                  <p className="text-[10px] text-purple-300">
                    {aiSubTasks.encouragement}
                  </p>
                </div>
              )}

              {/* Loading */}
              {isLoading && (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-4 h-4 text-purple-400 animate-spin" />
                  <span className="ml-2 text-[10px] text-zinc-400">Génération des étapes...</span>
                </div>
              )}

              {/* Sous-tâches IA */}
              {aiSubTasks?.subTasks && aiSubTasks.subTasks.map((step, idx) => {
                const isCompleted = completedAISteps.has(step.id);
                return (
                  <motion.div
                    key={step.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.03 }}
                    className="space-y-1"
                  >
                    {/* Étape principale */}
                    <div
                      onClick={(e) => { e.stopPropagation(); handleAIStepComplete(step.id); }}
                      className={`flex items-start gap-2 p-1.5 rounded transition-all cursor-pointer ${isCompleted ? 'opacity-50' : 'hover:bg-white/5'}`}
                    >
                      <div className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center transition-all flex-shrink-0 ${isCompleted ? 'bg-purple-600 border-purple-600 text-white' : 'bg-zinc-800/50 border-purple-500/50'}`}>
                        {isCompleted && <CheckCircle2 className="w-3 h-3" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className={`text-[11px] font-medium ${isCompleted ? 'line-through text-zinc-500' : 'text-zinc-300'}`}>
                          {step.title}
                        </span>
                        {step.description && !isCompleted && (
                          <p className="text-[9px] text-zinc-500 mt-0.5">
                            {step.description}
                          </p>
                        )}
                        {step.tip && !isCompleted && (
                          <p className="text-[9px] text-purple-400 mt-0.5 italic">
                            {step.tip}
                          </p>
                        )}
                      </div>
                      <span className="text-[9px] text-zinc-600 flex-shrink-0">
                        {step.duration}min
                      </span>
                    </div>

                    {/* Suggestion avec lien */}
                    {step.suggestion && !isCompleted && (
                      <div className="ml-6 p-1.5 rounded bg-cyan-500/10 border border-cyan-500/20">
                        <div className="flex items-start gap-1.5">
                          <span className="text-[10px]">💡</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-[10px] font-medium text-cyan-300">
                              {step.suggestion.title}
                            </p>
                            {step.suggestion.details && (
                              <p className="text-[9px] text-cyan-400/70">
                                {step.suggestion.details}
                              </p>
                            )}
                            {step.suggestion.url && (
                              <a
                                href={step.suggestion.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center gap-1 mt-1 text-[9px] text-cyan-400 hover:text-cyan-300 underline"
                              >
                                <ExternalLink className="w-2.5 h-2.5" />
                                Ouvrir
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </motion.div>
                );
              })}

              {/* Suggestion principale avec liens */}
              {aiSubTasks?.mainSuggestion && (
                <div className="mt-3 p-2 rounded bg-indigo-500/10 border border-indigo-500/20">
                  <p className="text-[10px] font-medium text-indigo-300 mb-1.5">
                    {aiSubTasks.mainSuggestion.title}
                  </p>
                  {aiSubTasks.mainSuggestion.items && (
                    <div className="space-y-1">
                      {aiSubTasks.mainSuggestion.items.map((item, idx) => (
                        <a
                          key={idx}
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="flex items-center gap-1.5 text-[9px] text-indigo-300 hover:text-indigo-200 transition-colors"
                        >
                          <span className="w-1 h-1 rounded-full bg-indigo-500" />
                          <span className="flex-1">{item.name}</span>
                          <ExternalLink className="w-2.5 h-2.5 opacity-50" />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Smart Links - Playlists, Vidéos, etc. */}
              {smartLinks && smartLinks.length > 0 && (
                <div className="mt-3 space-y-2">
                  {/* Playlists */}
                  {smartLinks.filter(l => l.type === 'playlist').length > 0 && (
                    <div className="p-2 rounded bg-green-500/10 border border-green-500/20">
                      <p className="text-[10px] font-medium text-green-300 mb-1.5 flex items-center gap-1.5">
                        <Music className="w-3 h-3" />
                        Musique recommandée
                      </p>
                      <div className="space-y-1">
                        {smartLinks.filter(l => l.type === 'playlist').map((link, idx) => (
                          <a
                            key={`playlist-${idx}`}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="flex items-center gap-2 text-[9px] text-green-300 hover:text-green-200 transition-colors"
                          >
                            <span>{link.icon}</span>
                            <span className="flex-1">{link.name}</span>
                            <span className="text-[8px] text-green-500 capitalize">{link.service}</span>
                            <ExternalLink className="w-2.5 h-2.5 opacity-50" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Vidéos YouTube */}
                  {smartLinks.filter(l => l.type === 'youtube').length > 0 && (
                    <div className="p-2 rounded bg-red-500/10 border border-red-500/20">
                      <p className="text-[10px] font-medium text-red-300 mb-1.5 flex items-center gap-1.5">
                        <Play className="w-3 h-3" />
                        Vidéos utiles
                      </p>
                      <div className="space-y-1">
                        {smartLinks.filter(l => l.type === 'youtube').slice(0, 3).map((link, idx) => (
                          <a
                            key={`video-${idx}`}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="flex items-center gap-2 text-[9px] text-red-300 hover:text-red-200 transition-colors"
                          >
                            <span className="w-1 h-1 rounded-full bg-red-500" />
                            <span className="flex-1">{link.name}</span>
                            {link.duration > 0 && (
                              <span className="text-[8px] text-red-500">{link.duration}min</span>
                            )}
                            <ExternalLink className="w-2.5 h-2.5 opacity-50" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Prime Video (récompenses) */}
                  {smartLinks.find(l => l.type === 'primeVideo') && (
                    <div className="p-2 rounded bg-blue-500/10 border border-blue-500/20">
                      <p className="text-[10px] font-medium text-blue-300 mb-1.5 flex items-center gap-1.5">
                        <Play className="w-3 h-3" />
                        Récompense - Prime Video
                      </p>
                      <div className="space-y-1">
                        {smartLinks.find(l => l.type === 'primeVideo').suggestions.slice(0, 3).map((item, idx) => (
                          <a
                            key={`prime-${idx}`}
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="flex items-center gap-2 text-[9px] text-blue-300 hover:text-blue-200 transition-colors"
                          >
                            <span className="w-1 h-1 rounded-full bg-blue-500" />
                            <span className="flex-1">{item.name}</span>
                            <ExternalLink className="w-2.5 h-2.5 opacity-50" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Rappel pause */}
              {aiSubTasks?.breakReminder && (
                <div className="p-1.5 rounded bg-amber-500/10 border border-amber-500/20">
                  <p className="text-[9px] text-amber-300">
                    {aiSubTasks.breakReminder}
                  </p>
                </div>
              )}

              {/* Bouton regénérer */}
              {aiSubTasks && (
                <button
                  onClick={(e) => { e.stopPropagation(); loadAISubTasks(); }}
                  className="flex items-center gap-1 text-[9px] text-zinc-500 hover:text-zinc-400 transition-colors mt-2"
                >
                  <RefreshCw className="w-2.5 h-2.5" />
                  Regénérer
                </button>
              )}

              {/* Message toutes étapes complétées */}
              {aiTotalSteps > 0 && aiCompletedCount === aiTotalSteps && (
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="p-2 rounded bg-green-500/20 border border-green-500/30 text-center"
                >
                  <p className="text-[10px] font-bold text-green-400">
                    🎉 Toutes les étapes complétées !
                  </p>
                  <p className="text-[9px] text-green-300 mt-0.5">
                    Tu peux être fier(e) de toi
                  </p>
                </motion.div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

export { SubTasksWithAI };
export default SubTasksWithAI;
